/**
 * SkyShipModule — the boardable sky-ship over every city (the mega-ship
 * wave). The spaceport ship no longer squats through the skyline: it rides
 * ~760 m up (CommonsSettlementsModule parks it aloft), and the boarding hall
 * at the old spaceport spot lifts you INTO it — a five-level walkable
 * interior (bridge / officer country + medbay / mess + berthing / engineering
 * + cargo / the huge foredeck) furnished with the generated `prop:ship_*`
 * kit and crewed by sixteen talkable hands with their own topic dialogue
 * about the ship and the city below.
 *
 * Regime notes (the altitude-interior laws):
 *  · `atlas.interiorId = 'skyship:<siteId>'` while aboard — the terrain clamp,
 *    the outdoor unstick watchdogs and the indoor single-rect clamp stand
 *    down (the ship registers no `enterables` entry and clamps itself).
 *    The reconcile HARD SNAP does NOT stand down: AtlasAvatarModule passes
 *    `RECONCILE_HARD_SNAP_M` to `decideCorrection` unconditionally and only
 *    the SOFT threshold changes indoors (0.8 m). That is deliberate — it is
 *    what makes the lift authoritative — but it means anything that moves
 *    the capsule aboard without moving the row is one accumulated 4 m of
 *    drift away from being teleported onto the row's column.
 *  · THE LIFT IS AUTHORITATIVE (2026-09-03): the `skyShipLift` reducer moves
 *    the row with the capsule (SKY_LIFT envelope) while reconcile is held —
 *    a lift the row did not ride was undone by the next hard snap, and the
 *    column it landed on held a deck only by luck (the fall-out field bug).
 *    Aboard, the row then walks with the player; its small corrections are
 *    advisory: a per-tick legality clamp (clampShipLocal) projects the
 *    capsule back into walkable rooms.
 *  · Stepping off the foredeck is a JUMP, not a bug: the fall watchdog
 *    clears the interior regime mid-air and you sky-dive down to the city.
 *  · The interior exists only while YOU are aboard (built on boarding,
 *    disposed on return) — from the ground the ship stays pure silhouette.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyIs, keyHint } from '../lib/keybinds.js';
import { InteractDedupe, typingTarget } from '../lib/interact_input.js';
import { deferDisposeTree, markShared } from '../lib/defer_dispose.js';
import { yieldToMain } from '../lib/yield_main.js';
import { makeRigActor, playRigClip, RIG_YAW_OFFSET, type RigActor } from '../lib/versona_rig.js';
import { clipFor } from '../lib/versona_clips.js';
import { activePreset } from '../lib/quality_tiers.js';import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { spaceportSpot, arrivalPoint } from '../lib/ports.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { normalFromAlbedo } from '../lib/normal_from_albedo.js';
import { sfx } from '../lib/sfx.js';
import { Logger } from 'enginex/core/Logger.js';
import {
  SHIP_ALT_M, ARRIVAL, RETURN_CONSOLE, SHIP_PROP_SPOTS, DECKS, SKY_LIFT, propTargetH,
  buildShipInterior, clampShipLocal, boardingGate, type ShipMats,
} from '../lib/skyship_interior.js';
import { skyShipName, skyShipCrew, crewTopics, crewGreeting, type CrewMember, type CrewArchetype, crewDuty } from '../lib/skyship_crew.js';
import { skyShipFrame } from '../lib/skyship_registry.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { uNightFactor } from './SkyModule.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { NpcInteractModule } from './NpcInteractModule.js';

const BOARD_RANGE_M = 12;
const TALK_RANGE_M = 3.8;
const FALL_OFF_M = 30;          // below the keel by this much = sky-diving
const CLAMP_EVERY = 5;          // legality projection cadence (fixed-ish ticks)

/** One hand aboard: the group at their feet (yaw = heading), the placeholder
 *  capsule, the generated figure once it lands, and the duty-route state. */
interface CrewHand {
  m: CrewMember;
  obj: THREE.Group;
  pill: THREE.Object3D;
  figure: THREE.Object3D | null;
  /** The skinned Meshy rig playing the duty's clip (near hands, capped by the tier) — the static figure hides under it. */
  actor: RigActor | null;
  plate: THREE.Sprite;
  wx: number; wy: number; wz: number;
  // Route state (ship-local).
  lx: number; lz: number; yaw: number;
  wp: number;                    // index of the waypoint being worked / walked to
  phase: 'dwell' | 'walk';
  t: number;                     // seconds in the phase
  fromX: number; fromZ: number;  // walk segment
  dist: number;
  speed: number;
  seed: number;                  // per-hand phase for fidgets
}

interface Aboard {
  site: SettlementSite;
  shipX: number; shipY: number; shipZ: number; yaw: number;
  group: THREE.Group;
  colliderIds: string[];
  crew: CrewHand[];
  releases: Array<() => void>;
  propsLoaded: number;
  /** Figure masters by archetype (loaded once per boarding; a missing catalog row retries later). `facing` turns the model's front to ship-local +Z. */
  figures: Map<CrewArchetype, { obj: THREE.Object3D; h: number; minY: number; facing: number }>;
  figuresTriedAt: number;
  /** Rig masters (`avatar-rig:crew_*`, 33 clips) per archetype — shared with the town's actor layer through the asset cache. */
  rigs: Map<CrewArchetype, { obj: THREE.Object3D; h: number }>;
  actorPlanAt: number;
}

/** Figure height aboard (m); each hand varies ±5 % by seed. */
const CREW_H = 1.82;
/** Hands within this ship-local distance of the player may wear a skinned actor (tier-capped). */
const ACTOR_RING_M = 45;
/** Working pace (m/s); deckhands and cargo hands stride faster. */
const CREW_WALK_MPS = 1.15;
/** Retry the crew-figure catalog this often while aboard (the generator lands models progressively). */
const FIGURE_RETRY_MS = 15_000;

export class SkyShipModule implements IModule {
  readonly name = 'SkyShip';
  private scene!: THREE.Scene;
  private seed = '';
  private sites: SettlementSite[] = [];
  private aboard: Aboard | null = null;
  private mats: ShipMats | null = null;
  private matsP: Promise<ShipMats> | null = null;
  private prompt: HTMLDivElement | null = null;
  private promptMode: 'board' | 'return' | 'talk' | null = null;
  private nearSite: SettlementSite | null = null;
  private nearCrew: { m: CrewMember; wx: number; wz: number } | null = null;
  private frame = 0;
  private boarding = false;
  private readonly plateMats = new Map<string, THREE.SpriteMaterial>();
  /** Lift-sync bookkeeping (probe surface `__skyship().lift`). */
  private readonly liftSync = { sent: 0, accepted: 0, rejected: 0, lastError: '' };

  constructor(
    private readonly assets: AssetModule,
    private readonly physics: PhysicsModule,
    private readonly env: EnvironmentModule,
    private readonly npcTalk: NpcInteractModule,
  ) {}

  init(app: App): void {
    this.scene = app.world.scene;
    app.world.addSystem(makeSystem('SkyShip', Phase.RenderSub, 40, () => this.tick()));
    // The lift answered the PHYSICAL E only: a pad or touch player, or anyone who rebound Interact, could not board or
    // return (and a rebound E still did). The bound key here, the pad's press on the bus; one press is one act.
    const once = new InteractDedupe();
    const liftAct = (): boolean => {
      if (this.promptMode === 'board' && this.nearSite) { void this.board(this.nearSite); return true; }
      if (this.promptMode === 'return') { this.disembark(true); return true; }
      return false;
    };
    bus.on('atlas:place-requested', () => { if (!typingTarget(document.activeElement) && once.accept(performance.now())) liftAct(); });
    window.addEventListener('keydown', (e) => {
      const interact = keyIs(e.code, 'interact');
      if (!interact && e.code !== 'KeyT') return;
      const typing = document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);
      if (typing) return;
      if (interact) {
        if (!e.repeat && !e.ctrlKey && !e.metaKey && once.accept(performance.now()) && liftAct()) e.stopPropagation();
      } else if (e.code === 'KeyT' && this.aboard && this.nearCrew) {
        this.talkTo(this.nearCrew.m);
        e.stopPropagation();
      }
    }, { capture: true });
    // Probe/diagnostic surface.
    (window as unknown as Record<string, unknown>).__skyship = () => ({
      aboard: this.aboard ? this.aboard.site.id : null,
      // The cities that fly a ship (civ A — the city-hall cities) + their pads.
      spots: this.sites.map((s) => {
        const sp = spaceportSpot(this.commonsSeed(), s);
        return { id: s.id, civ: civilizationOf(s.siteSeed), x: sp ? s.centerX + sp.x : null, z: sp ? s.centerZ + sp.z : null };
      }),
      props: this.aboard?.propsLoaded ?? 0,
      crew: this.aboard?.crew.length ?? 0,
      colliders: this.aboard?.colliderIds.length ?? 0,
      lift: { ...this.liftSync },
      // The hull frames the settlement streamer has registered (measured on
      // placement; the interior anchors to them). Absent = analytic pad + SHIP_ALT_M.
      frames: this.sites.map((s) => ({ id: s.id, frame: skyShipFrame(s.id) })).filter((f) => f.frame),
      shipY: this.aboard?.shipY ?? 0,
      shipX: this.aboard?.shipX ?? 0,
      shipZ: this.aboard?.shipZ ?? 0,
      yaw: this.aboard?.yaw ?? 0,
      prompt: this.promptMode,
      board: (siteId?: number) => {
        const s = this.sites.find((x) => x.id === (siteId ?? this.nearSite?.id));
        if (s) void this.board(s);
        return !!s;
      },
      leave: () => this.disembark(true),
      crewNames: this.aboard?.crew.map((c) => `${c.m.name} (${c.m.roleTitle})`) ?? [],
      // The working crew (probe): how many wear a generated figure, how many are mid-stride, where each stands (ship-local).
      crewStats: this.aboard ? {
        hands: this.aboard.crew.length,
        figures: this.aboard.crew.filter((c) => c.figure).length,
        archetypesLoaded: [...this.aboard.figures.keys()],
        rigsLoaded: [...this.aboard.rigs.keys()],
        actors: this.aboard.crew.filter((c) => c.actor).length,
        actorClips: this.aboard.crew.filter((c) => c.actor).slice(0, 8).map((c) => `${c.m.archetype}:${c.actor?.currentName}`),
        walking: this.aboard.crew.filter((c) => c.phase === 'walk').length,
        sample: this.aboard.crew.map((c) => ({ idx: c.m.idx, lx: +c.lx.toFixed(2), lz: +c.lz.toFixed(2), phase: c.phase, wp: c.wp, fig: !!c.figure })),
      } : null,
      talk: (idx: number) => {
        const c = this.aboard?.crew.find((x) => x.m.idx === idx);
        if (c) this.talkTo(c.m);
        return !!c;
      },
      // PROBE LEVER: warp aboard to a ship-local point (the deck tour — the
      // headless sim runs a few ticks per second, so a probe cannot WALK the
      // 900 m interior). Moves the capsule (explicit Y) and the ROW together
      // (debugTeleport, gated by the debug_reducers platform flag) under the
      // same reconcile hold the lift uses — a warp the row did not ride would
      // be undone by the hard snap exactly like the pre-fix lift.
      warp: (lx: number, ly: number, lz: number) => {
        const a = this.aboard;
        const conn = atlas.conn;
        if (!a || !conn) return false;
        const cos = Math.cos(a.yaw), sin = Math.sin(a.yaw);
        const wx = a.shipX + lx * cos + lz * sin;
        const wz = a.shipZ - lx * sin + lz * cos;
        bus.emit('atlas:reconcile-hold', { ticks: SKY_LIFT.holdTicks, mode: 'hold' });
        this.physics.teleportAvatarY(wx, a.shipY + ly + 0.2, wz);
        const rr = conn.reducers as unknown as { debugTeleport?: (x: { posX: number; posZ: number }) => Promise<void> };
        if (typeof rr.debugTeleport !== 'function') { bus.emit('atlas:reconcile-hold', { ticks: 0, mode: 'settle' }); return false; }
        rr.debugTeleport({ posX: wx, posZ: wz }).then(
          () => bus.emit('atlas:reconcile-hold', { ticks: SKY_LIFT.settleTicks, mode: 'settle' }),
          () => bus.emit('atlas:reconcile-hold', { ticks: 0, mode: 'settle' }));
        return true;
      },
    });
  }

  private ensurePrompt(): HTMLDivElement {
    if (this.prompt) return this.prompt;
    const d = document.createElement('div');
    d.style.cssText = 'position:fixed;left:50%;bottom:264px;transform:translateX(-50%);padding:6px 14px;'
      + 'background:color-mix(in srgb, var(--ax-bg) 82%, transparent);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);'
      + 'font:600 13px var(--ax-font);z-index:42;display:none;pointer-events:none';
    document.body.appendChild(d);
    this.prompt = d;
    return d;
  }

  private setPrompt(mode: 'board' | 'return' | 'talk' | null, text = ''): void {
    this.promptMode = mode;
    const d = this.ensurePrompt();
    if (!mode) { d.style.display = 'none'; return; }
    d.textContent = text;
    d.style.display = 'block';
  }

  private commonsSeed(): string {
    if (this.seed) return this.seed;
    const conn = atlas.conn;
    if (conn) {
      for (const w of conn.db.world.iter()) {
        if (w.kind === 'commons') {
          this.seed = w.terrainSeed;
          // Civ-A cities only: the ship IS the Aurelian city hall.
          this.sites = settlementSites(this.seed).filter((s) => s.tier === 'city' && civilizationOf(s.siteSeed) === 'a');
          break;
        }
      }
    }
    return this.seed;
  }

  // ── Materials (generated texture tiles, flat fallbacks) ──────────────────
  private ensureMats(): Promise<ShipMats> {
    if (this.matsP) return this.matsP;
    this.matsP = (async () => {
      const std = (color: number, rough = 0.85): THREE.MeshStandardMaterial =>
        new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.15 });
      const tex = async (key: string, mat: THREE.MeshStandardMaterial, n = 2.0): Promise<void> => {
        try {
          const t = await this.assets.loadTexture(`textures/${key}.png`);
          t.wrapS = t.wrapT = THREE.RepeatWrapping;
          mat.map = t;
          const nm = normalFromAlbedo(t, n);
          if (nm) { mat.normalMap = nm; mat.normalScale = new THREE.Vector2(0.5, 0.5); }
          mat.color = new THREE.Color(0xffffff);
          mat.needsUpdate = true;
        } catch { /* flat fallback */ }
      };
      const deck = std(0x6b7076, 0.9);
      const wall = std(0xb9b4a6, 0.92);
      const ceiling = std(0x3a3f45, 0.95);
      const bridgeFloor = std(0x2c3a52, 0.8);
      const grate = std(0x4a4640, 0.95);
      const cargoFloor = std(0x5a554a, 0.95);
      const quartersWall = std(0xc9b8a0, 0.95);
      const hull = std(0x4a4038, 0.85);
      const foredeck = std(0x767c84, 0.9);
      await Promise.all([
        tex('ship_deck_plate', deck), tex('ship_wall_panel', wall), tex('ship_ceiling_panel', ceiling, 1.4),
        tex('ship_bridge_floor', bridgeFloor), tex('ship_engine_grate', grate, 2.6),
        tex('ship_cargo_floor', cargoFloor), tex('ship_quarters_wall', quartersWall, 1.4),
        tex('ship_hull_exterior', hull, 2.8), tex('ship_deck_plate', foredeck),
      ]);
      const glass = new THREE.MeshStandardMaterial({
        color: 0x9fd0ff, transparent: true, opacity: 0.18, roughness: 0.1, metalness: 0.2, side: THREE.DoubleSide,
      });
      const glow = markMrtOverlay(new THREE.MeshBasicMaterial({ color: 0xffd9a0, transparent: true, opacity: 0.85, side: THREE.DoubleSide }));
      const gold = std(0xb98a3c, 0.5);
      const dark = std(0x2e3238, 0.9);
      this.mats = { deck, wall, ceiling, bridgeFloor, grate, cargoFloor, quartersWall, hull, foredeck, glass, glow, gold, dark };
      // Module-lifetime set reused on every boarding, textures from the
      // AssetModule cache: stamp SHARED so teardown's deferDisposeTree frees
      // only the interior's own geometry (it freed these — a re-upload +
      // pipeline recompile of every material on each disembark).
      for (const m of Object.values(this.mats) as THREE.Material[]) {
        m.userData.shared = true;
        const sm = m as THREE.MeshStandardMaterial;
        for (const t of [sm.map, sm.normalMap]) if (t) t.userData.shared = true;
      }
      return this.mats;
    })();
    return this.matsP;
  }

  // ── Boarding ─────────────────────────────────────────────────────────────
  private async board(site: SettlementSite): Promise<void> {
    if (this.aboard || this.boarding) return;
    // THE RIDE HANDOFF (boardingGate): a flying ship never boards — the
    // prompt already says so; the hovercraft is stepped off right before
    // the lift, below.
    if (boardingGate(atlas.hoverRide, atlas.skyFlight) === 'land-first') return;
    this.boarding = true;
    try {
      const seed = this.commonsSeed();
      const sport = spaceportSpot(seed, site);
      if (!sport) return;
      // The interior anchors to the hull the settlement actually parked
      // (measured frame: decks ride the upper hull, the foredeck opens onto
      // her back); before the ship assembles, the analytic pad + SHIP_ALT_M.
      // The interior's bow (local +Z) runs along the hull's LONG axis: mesh Z
      // shares the placement yaw, mesh X takes a quarter-turn.
      const frame = skyShipFrame(site.id);
      const shipX = frame ? frame.x : site.centerX + sport.x;
      const shipZ = frame ? frame.z : site.centerZ + sport.z;
      const yaw = frame ? (frame.longAxisX ? frame.yaw - Math.PI / 2 : frame.yaw) : sport.yaw;
      const shipY = frame ? frame.deckBaseY : meshHeight(seed, shipX, shipZ) + SHIP_ALT_M;
      const M = await this.ensureMats();
      // Step off the skimmer NOW, after the only await and before anything is
      // built: the hover integrator owns atlas.localY against the TERRAIN and
      // the avatar capsule parks while it rides, so a lift taken in the
      // saddle sank straight back through the decks to the ground (field
      // report 2026-09-02; probe: aboard with hoverRide=true). The bus is
      // synchronous — hoverRide is false on return; a ride that refused to
      // end (a flight resumed during the await) abandons the lift instead.
      if (boardingGate(atlas.hoverRide, atlas.skyFlight) === 'dismount') bus.emit('atlas:vehicle-dismount', { why: `skyship:${site.id}` });
      if (atlas.hoverRide || atlas.skyFlight) return;
      const built = buildShipInterior(M);
      built.group.position.set(shipX, shipY, shipZ);
      built.group.rotation.y = yaw;
      this.scene.add(built.group);
      // Colliders: compose each local box with the ship frame.
      const cos = Math.cos(yaw), sin = Math.sin(yaw);
      const colliderIds: string[] = [];
      built.colliders.forEach((c, i) => {
        const wx = shipX + c.x * cos + c.z * sin;
        const wz = shipZ - c.x * sin + c.z * cos;
        const id = `skyship:${site.id}:${i}`;
        this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [c.hx, c.hy, c.hz] },
          { x: wx, y: shipY + c.y, z: wz, yaw: yaw + c.yaw, scale: 1 });
        colliderIds.push(id);
      });
      const aboard: Aboard = {
        site, shipX, shipY, shipZ, yaw, group: built.group,
        colliderIds, crew: [], releases: [], propsLoaded: 0,
        figures: new Map(), figuresTriedAt: 0, rigs: new Map(), actorPlanAt: 0,
      };
      this.aboard = aboard;
      // The regime flip + the lift itself.
      atlas.interiorId = `skyship:${site.id}`;
      const ax = shipX + ARRIVAL.x * cos + ARRIVAL.z * sin;
      const az = shipZ - ARRIVAL.x * sin + ARRIVAL.z * cos;
      this.physics.teleportAvatarY(ax, shipY + ARRIVAL.y + 0.2, az);
      this.syncRow(site, ax, az, true); // the row rides the lift (synchronous hold, async landing)
      try { sfx('sfx_teleport'); } catch { /* no audio yet */ }
      this.setPrompt(null);
      // Furnish + crew stream in AFTER arrival (never block the lift).
      void this.furnish(aboard);
      this.spawnCrew(aboard);
    } finally {
      this.boarding = false;
    }
  }

  /** Stream the prop kit in (masters cached by the asset module; a missing
   *  catalog row skips — the wave may still be generating that piece). */
  private async furnish(aboard: Aboard): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const keyToAsset = new Map<string, bigint>();
    for (const pm of conn.db.prefabModel.iter()) keyToAsset.set(pm.prefabKey, pm.assetId);
    const cos = Math.cos(aboard.yaw), sin = Math.sin(aboard.yaw);
    const masters = new Map<string, { obj: THREE.Object3D; h: number }>();
    for (const spot of SHIP_PROP_SPOTS) {
      if (this.aboard !== aboard) return; // disembarked mid-stream
      const assetId = keyToAsset.get(spot.key);
      if (assetId === undefined) continue;
      let m = masters.get(spot.key);
      if (!m) {
        try {
          const r = await this.assets.loadRepresentation(assetId, 0);
          if (this.aboard !== aboard) { r.release(); return; } // disembarked mid-load: teardown already ran its release list (2026-09-12)
          markShared(r.object); // clones tear down freely; the cache master's buffers survive
          const bb = new THREE.Box3().setFromObject(r.object);
          aboard.releases.push(r.release);
          m = { obj: r.object, h: Math.max(0.2, bb.max.y - bb.min.y) };
          masters.set(spot.key, m);
        } catch { continue; }
      }
      if (this.aboard !== aboard) return;
      const inst = m.obj.clone();
      // THE DECOR READS SMALL: fit-scale to the room-clamped, scaled silhouette (propTargetH).
      const sc = Math.max(0.2, Math.min(8, propTargetH(spot) / m.h));
      inst.scale.setScalar(sc);
      inst.position.set(spot.x, spot.y, spot.z);
      inst.rotation.y = spot.yaw;
      aboard.group.add(inst);
      if (spot.solid) {
        const bb = new THREE.Box3().setFromObject(m.obj);
        const hx = Math.max(0.15, (bb.max.x - bb.min.x) / 2 * sc);
        const hy = Math.max(0.15, (bb.max.y - bb.min.y) / 2 * sc);
        const hz = Math.max(0.15, (bb.max.z - bb.min.z) / 2 * sc);
        const wx = aboard.shipX + spot.x * cos + spot.z * sin;
        const wz = aboard.shipZ - spot.x * sin + spot.z * cos;
        const id = `skyship:${aboard.site.id}:p${aboard.propsLoaded}`;
        this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [hx, hy, hz] },
          { x: wx, y: aboard.shipY + spot.y + hy, z: wz, yaw: aboard.yaw + spot.yaw, scale: 1 });
        aboard.colliderIds.push(id);
      }
      aboard.propsLoaded++;
      await yieldToMain(); // one prop per yield — no hitch (scheduler.yield when available)
    }
  }

  /** The crew, at their stations, name-plated and talkable — a capsule
   *  placeholder each until their generated figure lands (dressCrew). */
  private spawnCrew(aboard: Aboard): void {
    const seed = this.commonsSeed();
    const cos = Math.cos(aboard.yaw), sin = Math.sin(aboard.yaw);
    for (const m of skyShipCrew(seed, aboard.site)) {
      const start = m.route[0] ?? { x: m.x, z: m.z, yaw: m.yaw, dwell: 10 };
      const body = new THREE.Group();
      body.name = `crew:${m.idx}`;
      const pill = this.env.bodyFor(`skyship:${aboard.site.id}:${m.idx}`);
      body.add(pill);
      body.position.set(start.x, m.y, start.z);
      body.rotation.y = start.yaw;
      // Nameplate sprite.
      const mat = this.plateMat(`${m.name} · ${m.roleTitle}`);
      const plate = new THREE.Sprite(mat);
      const img = mat.map?.image as { width: number; height: number } | undefined;
      plate.scale.set(((img?.width ?? 200) / (img?.height ?? 40)) * 0.34, 0.34, 1);
      plate.position.y = 2.15;
      body.add(plate);
      aboard.group.add(body);
      const wx = aboard.shipX + start.x * cos + start.z * sin;
      const wz = aboard.shipZ - start.x * sin + start.z * cos;
      const h01 = ((m.idx * 2654435761) >>> 0) / 4294967296;
      aboard.crew.push({
        m, obj: body, pill, figure: null, actor: null, plate, wx, wy: aboard.shipY + m.y, wz,
        lx: start.x, lz: start.z, yaw: start.yaw, wp: 0, phase: 'dwell', t: h01 * start.dwell, // staggered starts
        fromX: start.x, fromZ: start.z, dist: 0,
        speed: CREW_WALK_MPS * ((m.role === 'deckhand' || m.role === 'cargohand') ? 1.15 : 1) * (0.92 + 0.16 * h01),
        seed: h01,
      });
    }
    void this.dressCrew(aboard);
  }

  /**
   * THE CREW ARE PEOPLE (field report 2026-09-03: "they are just pill shaped
   * primitive shapes — generate actual glb models of crew people"). One
   * generated figure per archetype (`avatar:crew_<archetype>`, the
   * gen-skyship-crew.mjs wave) is loaded once per boarding and cloned onto
   * every hand of that archetype: fit-scaled to CREW_H (±5 % by seed), feet on
   * the deck, the capsule hidden. A figure the catalog does not carry yet
   * keeps its capsule and is retried every FIGURE_RETRY_MS (the wave lands
   * models one by one).
   */
  private async dressCrew(aboard: Aboard): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    aboard.figuresTriedAt = performance.now();
    const keyToAsset = new Map<string, bigint>();
    for (const pm of conn.db.prefabModel.iter()) keyToAsset.set(pm.prefabKey, pm.assetId);
    const wanted = new Set<CrewArchetype>();
    for (const c of aboard.crew) if (!c.figure || !aboard.rigs.has(c.m.archetype)) wanted.add(c.m.archetype);
    for (const arch of wanted) {
      // THE CREW WEAR THE RIGS (2026-09-07): the rigged master (33 clips) per archetype, cache-shared with the
      // town layer; a hand near the player becomes a skinned actor (planCrewActors), the rest keep the figure.
      if (!aboard.rigs.has(arch)) {
        const rigId = keyToAsset.get(`avatar-rig:crew_${arch}`);
        if (rigId !== undefined) {
          try {
            const rig = await this.assets.load(rigId);
            if (this.aboard !== aboard) { this.assets.dispose(rigId); return; }
            if ((rig.animations ?? []).length > 0) {
              markShared(rig);
              aboard.releases.push(() => this.assets.dispose(rigId));
              const bb = new THREE.Box3().setFromObject(rig);
              aboard.rigs.set(arch, { obj: rig, h: Math.max(0.2, bb.max.y - bb.min.y) });
            } else this.assets.dispose(rigId);
          } catch { /* the static figure stands in */ }
        }
      }
      if (aboard.crew.every((c) => c.figure || c.m.archetype !== arch)) { await yieldToMain(); continue; }
      if (this.aboard !== aboard) return;
      let f = aboard.figures.get(arch) ?? null;
      if (!f) {
        const assetId = keyToAsset.get(`avatar:crew_${arch}`);
        if (assetId === undefined) continue; // not generated yet — the capsule stands in
        try {
          const r = await this.assets.loadRepresentation(assetId, 0);
          if (this.aboard !== aboard) { r.release(); return; }
          markShared(r.object);
          aboard.releases.push(r.release);
          const bb = new THREE.Box3().setFromObject(r.object);
          f = { obj: r.object, h: Math.max(0.2, bb.max.y - bb.min.y), minY: bb.min.y, facing: CREW_FIGURE_FACING };
          aboard.figures.set(arch, f);
        } catch { continue; }
      }
      for (const c of aboard.crew) {
        if (c.figure || c.m.archetype !== arch) continue;
        const inst = f.obj.clone();
        const sc = (CREW_H * (0.95 + 0.1 * c.seed)) / f.h;
        inst.scale.setScalar(sc);
        inst.position.y = -f.minY * sc; // feet on the deck
        inst.rotation.y = f.facing;
        c.obj.add(inst);
        c.figure = inst;
        c.pill.visible = false;
        c.plate.position.y = CREW_H * (0.95 + 0.1 * c.seed) + 0.32;
      }
      await yieldToMain();
    }
  }

  /**
   * THE CREW ARE BUSY: every hand walks its duty route (skyship_crew.ts) —
   * dwell at a waypoint facing the work with small fidgets (a slow sway, a
   * glance aside), then stride to the next at a working pace with a step bob
   * and a forward lean. Unrigged figures, so the motion is the body's, not
   * the limbs' — but a deck full of people moving between consoles, crates
   * and bunks reads as a working ship. Ship-local math; the group carries the
   * heading, the figure carries bob + lean so the nameplate stays level.
   */
  private stepCrew(aboard: Aboard, dt: number): void {
    const cos = Math.cos(aboard.yaw), sin = Math.sin(aboard.yaw);
    for (const c of aboard.crew) {
      const route = c.m.route;
      if (route.length === 0) continue;
      c.t += dt;
      const wp = route[c.wp % route.length];
      let bob = 0, lean = 0, sway = 0;
      if (c.phase === 'dwell') {
        // Face the work; fidget: a slow sway and a glance aside every few seconds.
        const glance = Math.sin(c.t * 0.9 + c.seed * 6.28) > 0.93 ? 0.55 : 0;
        const target = wp.yaw + 0.12 * Math.sin(c.t * 2.1 + c.seed * 6.28) + glance;
        c.yaw += shortAngle(target - c.yaw) * Math.min(1, dt * 4);
        bob = 0.012 * Math.sin(c.t * 1.3 + c.seed * 3);
        if (c.t >= wp.dwell && route.length > 1) {
          const next = route[(c.wp + 1) % route.length];
          c.phase = 'walk'; c.t = 0;
          c.fromX = c.lx; c.fromZ = c.lz;
          c.dist = Math.max(0.01, Math.hypot(next.x - c.lx, next.z - c.lz));
        }
      } else {
        const next = route[(c.wp + 1) % route.length];
        const u = Math.min(1, (c.t * c.speed) / c.dist);
        c.lx = c.fromX + (next.x - c.fromX) * u;
        c.lz = c.fromZ + (next.z - c.fromZ) * u;
        const heading = Math.atan2(next.x - c.fromX, next.z - c.fromZ);
        c.yaw += shortAngle(heading - c.yaw) * Math.min(1, dt * 6);
        const stepHz = 1.9;
        bob = 0.035 * Math.abs(Math.sin(c.t * stepHz * Math.PI));
        lean = 0.07;
        sway = 0.03 * Math.sin(c.t * stepHz * Math.PI);
        if (u >= 1) { c.phase = 'dwell'; c.t = 0; c.wp = (c.wp + 1) % route.length; }
      }
      c.obj.position.set(c.lx, c.m.y, c.lz);
      c.obj.rotation.y = c.yaw;
      if (c.actor) {
        // the rig carries the motion: no body bob / lean; the duty phase picks the clip through the shared grammar
        const duty = crewDuty(c.m.archetype, c.phase);
        const choice = clipFor(duty.state, { moving: c.phase === 'walk', talking: duty.talking, phase: c.seed * 6.28, parity: c.m.idx });
        playRigClip(c.actor, choice.clip, choice.loop, c.phase === 'walk' ? choice.speed * (c.speed / 1.3) : choice.speed);
        c.actor.mixer.update(dt);
      } else {
      const fig = c.figure ?? c.pill;
      fig.position.y = (c.figure ? -this.figureMinY(aboard, c) : 0) + bob;
      fig.rotation.x = lean;
      fig.rotation.z = sway;
      }
      c.wx = aboard.shipX + c.lx * cos + c.lz * sin;
      c.wz = aboard.shipZ - c.lx * sin + c.lz * cos;
    }
  }
  /**
   * Which hands wear a skinned actor: the nearest to the player (ship-local), capped by the tier's
   * `nearActors`, within ACTOR_RING_M; the others show their static figure. Re-planned every 0.5 s.
   */
  private planCrewActors(aboard: Aboard): void {
    const now = performance.now();
    if (now - aboard.actorPlanAt < 500) return;
    aboard.actorPlanAt = now;
    if (aboard.rigs.size === 0) return;
    const cos = Math.cos(aboard.yaw), sin = Math.sin(aboard.yaw);
    const dx = atlas.localX - aboard.shipX, dz = atlas.localZ - aboard.shipZ;
    const px = dx * cos - dz * sin, pz = dx * sin + dz * cos; // ship-local player
    const cap = Math.max(0, activePreset().nearActors ?? 24);
    const cands = aboard.crew.filter((c) => aboard.rigs.has(c.m.archetype)).map((c) => ({ c, d: Math.hypot(c.lx - px, c.lz - pz) })).sort((a, b) => a.d - b.d);
    const keep = new Set<CrewHand>();
    for (let i = 0; i < cands.length && i < cap; i++) if (cands[i].d < ACTOR_RING_M) keep.add(cands[i].c);
    for (const c of aboard.crew) {
      if (keep.has(c)) { if (!c.actor) this.ensureActor(aboard, c); }
      else if (c.actor) this.dropActor(c);
    }
  }
  private ensureActor(aboard: Aboard, c: CrewHand): void {
    const rig = aboard.rigs.get(c.m.archetype); if (!rig) return;
    const a = makeRigActor(rig.obj, `crew_${c.m.archetype}`, `crew:${c.m.idx}`);
    const sc = (CREW_H * (0.95 + 0.1 * c.seed)) / rig.h;
    a.root.scale.setScalar(sc);
    a.root.rotation.y = RIG_YAW_OFFSET + CREW_FIGURE_FACING;
    c.obj.add(a.root);
    c.actor = a;
    if (c.figure) c.figure.visible = false;
    c.pill.visible = false;
  }
  private dropActor(c: CrewHand): void {
    const a = c.actor; if (!a) return;
    a.mixer.stopAllAction();
    c.obj.remove(a.root);
    c.actor = null;
    if (c.figure) c.figure.visible = true; else c.pill.visible = true;
  }

  private figureMinY(aboard: Aboard, c: CrewHand): number {
    const f = aboard.figures.get(c.m.archetype);
    return f ? f.minY * (c.figure?.scale.x ?? 1) : 0;
  }

  private plateMat(text: string): THREE.SpriteMaterial {
    const hit = this.plateMats.get(text);
    if (hit) return hit;
    const font = 26;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `600 ${font}px system-ui`;
    canvas.width = Math.ceil(ctx.measureText(text).width) + 26;
    canvas.height = font + 16;
    ctx.font = `600 ${font}px system-ui`;
    ctx.fillStyle = 'rgba(8,12,20,0.72)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffe2ad';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 13, canvas.height / 2);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
    mat.userData.shared = true; // module-lifetime cache — teardown must not free it
    tex.userData.shared = true;
    this.plateMats.set(text, mat);
    return mat;
  }

  private talkTo(m: CrewMember): void {
    if (!this.aboard) return;
    const seed = this.commonsSeed();
    this.npcTalk.openShipCrew({
      name: m.name,
      title: `${m.roleTitle} · ${skyShipName(seed, this.aboard.site)}`,
      greeting: crewGreeting(seed, this.aboard.site, m),
      topics: crewTopics(seed, this.aboard.site, m).map((t) => ({ k: t.key, l: t.label, r: t.text })),
    });
  }

  // ── The per-frame watch ──────────────────────────────────────────────────
  private tick(): void {
    if (atlas.currentWorldId !== atlas.commonsWorldId) {
      if (this.aboard) this.teardown();
      return;
    }
    this.frame++;
    const seed = this.commonsSeed();
    if (!seed) return;

    if (!this.aboard) {
      if (this.frame % 12 !== 0) return;
      // Ground: the boarding prompt near any city's spaceport spot.
      let near: SettlementSite | null = null;
      for (const s of this.sites) {
        const d2 = (s.centerX - atlas.localX) ** 2 + (s.centerZ - atlas.localZ) ** 2;
        if (d2 > 400 * 400) continue;
        const sp = spaceportSpot(seed, s);
        if (!sp) continue;
        const d = Math.hypot(s.centerX + sp.x - atlas.localX, s.centerZ + sp.z - atlas.localZ);
        if (d < BOARD_RANGE_M) { near = s; break; }
      }
      this.nearSite = near;
      if (near) {
        const name = skyShipName(seed, near);
        // A flying ship must land first (boardingGate); a hovercraft is fine
        // — the lift steps you off it.
        this.setPrompt('board', atlas.skyFlight ? `Land your ship to board the ${name}` : `[${keyHint('interact')}] Board the ${name}`);
      } else if (this.promptMode === 'board') this.setPrompt(null);
      return;
    }

    // Aboard.
    const a = this.aboard;
    // Night glow follows the sky (the shared-material law).
    const glow = this.mats?.glow as THREE.MeshBasicMaterial | undefined;
    if (glow) glow.opacity = 0.55 + 0.45 * (uNightFactor.value as number);

    // Ship-local frame of the player.
    const dx = atlas.localX - a.shipX;
    const dz = atlas.localZ - a.shipZ;
    const cos = Math.cos(a.yaw), sin = Math.sin(a.yaw);
    const lx = dx * cos - dz * sin;
    const lz = dx * sin + dz * cos;
    const ly = atlas.localY - a.shipY;

    // Sky-dive watch: below the keel = deliberately off the deck.
    if (ly < DECKS.engineering.y - FALL_OFF_M) {
      this.teardown(); // clears interiorId → the normal ground regime resumes mid-air
      return;
    }
    // Legality clamp (the reconcile-advisory law) every few frames.
    if (this.frame % CLAMP_EVERY === 0) {
      const c = clampShipLocal(lx, lz, ly);
      if (c.clamped) {
        const wx = a.shipX + c.x * cos + c.z * sin;
        const wz = a.shipZ - c.x * sin + c.z * cos;
        atlas.localX = wx;
        atlas.localZ = wz;
        this.physics.teleportAvatar(wx, wz, true);
      }
    }
    // Prompts: return console > nearest crew.
    if (this.frame % 6 === 0) {
      const dRet = Math.hypot(lx - RETURN_CONSOLE.x, lz - RETURN_CONSOLE.z);
      if (dRet < 3.2 && Math.abs(ly - RETURN_CONSOLE.y) < 2.5) {
        this.nearCrew = null;
        this.setPrompt('return', `[${keyHint('interact')}] Return to ${cityNameOf(seed, a.site)}`);
      } else {
        let best: { m: CrewMember; wx: number; wz: number } | null = null;
        let bestD = TALK_RANGE_M;
        for (const c of a.crew) {
          if (Math.abs(c.wy - atlas.localY) > 3) continue;
          const d = Math.hypot(c.wx - atlas.localX, c.wz - atlas.localZ);
          if (d < bestD) { bestD = d; best = { m: c.m, wx: c.wx, wz: c.wz }; }
        }
        this.nearCrew = best;
        if (best) this.setPrompt('talk', `[T] Talk to ${best.m.name}, ${best.m.roleTitle}`);
        else this.setPrompt(null);
      }
    }
    // Crew life: duty routes (walk / dwell / fidget) every frame; figures the
    // catalog gained since boarding are picked up on a slow retry.
    this.planCrewActors(a);
    this.stepCrew(a, Math.min(0.1, this.frameDt()));
    if (performance.now() - a.figuresTriedAt > FIGURE_RETRY_MS && a.crew.some((c) => !c.figure || !a.rigs.has(c.m.archetype))) void this.dressCrew(a);
  }

  private lastFrameAt = 0;
  private frameDt(): number {
    const now = performance.now();
    const dt = this.lastFrameAt ? (now - this.lastFrameAt) / 1000 : 1 / 60;
    this.lastFrameAt = now;
    return dt;
  }

  private disembark(toPad: boolean, sync = true): void {
    const a = this.aboard;
    if (!a) return;
    const seed = this.commonsSeed();
    this.teardown();
    if (toPad) {
      // EXPLICIT ground seat (probe5 field find): the grounded teleport path
      // left the capsule at altitude in the same frame the interior colliders
      // were being removed — an explicit Y from the rendered ground is
      // unambiguous, and the terrain clamp fine-tunes from there.
      const p = arrivalPoint(seed, a.site);
      this.physics.teleportAvatarY(p.x, meshHeight(seed, p.x, p.z) + 0.3, p.z);
      if (sync) this.syncRow(a.site, p.x, p.z, false); // the row comes down with you
      try { sfx('sfx_teleport'); } catch { /* no audio */ }
    }
  }

  /**
   * THE LIFT IS AUTHORITATIVE (field report 2026-09-03: "whenever I move in
   * the sky ship … I fall through the floor … and fall to the city"). The
   * row must ride the lift with the capsule: reconcile compares the row with
   * the client's position whenever nothing is in flight, so a row left on
   * the pad read the 288 m to the airlock as a teleport and hard-snapped the
   * capsule back over the pad a tick later — a column that holds a deck only
   * by luck (Ostorch: the crew deck; a row that trailed a hovercraft: open
   * air). Hold reconcile while the reducer is in flight (the unstick-nudge
   * pattern), settle when it lands; a refusal reverts the lift to the row
   * (server authority, never a drag).
   */
  private syncRow(site: SettlementSite, x: number, z: number, aboard: boolean): void {
    const conn = atlas.conn;
    const rr = conn?.reducers as unknown as { skyShipLift?: (a: { siteId: number; posX: number; posZ: number; aboard: boolean }) => Promise<void> } | undefined;
    if (!rr || typeof rr.skyShipLift !== 'function') {
      Logger.warn('SkyShip', 'skyShipLift reducer missing (stale bindings) — the row stays behind and reconcile will snap');
      return;
    }
    bus.emit('atlas:reconcile-hold', { ticks: SKY_LIFT.holdTicks, mode: 'hold' });
    this.liftSync.sent++;
    rr.skyShipLift({ siteId: site.id, posX: x, posZ: z, aboard }).then(() => {
      this.liftSync.accepted++;
      bus.emit('atlas:reconcile-hold', { ticks: SKY_LIFT.settleTicks, mode: 'settle' });
    }, (e: unknown) => {
      this.liftSync.rejected++;
      this.liftSync.lastError = String((e as Error)?.message ?? e).slice(0, 80);
      Logger.warn('SkyShip', `${aboard ? 'lift' : 'return'} refused by the server (${this.liftSync.lastError}) — reverting to the row`);
      bus.emit('atlas:reconcile-hold', { ticks: 0, mode: 'settle' });
      bus.emit('atlas:status', { text: `${aboard ? 'The lift' : 'The airlock'} refused: ${this.liftSync.lastError}` });
      // The row never moved: stand where the server says, on the ground.
      if (aboard && this.aboard?.site.id === site.id) this.teardown();
      const me = conn?.identity ? conn.db.playerState.identity.find(conn.identity) : null;
      if (me) { atlas.localX = me.posX; atlas.localZ = me.posZ; this.physics.teleportAvatar(me.posX, me.posZ); }
    });
  }

  private teardown(): void {
    const a = this.aboard;
    if (!a) return;
    this.aboard = null;
    if (atlas.interiorId === `skyship:${a.site.id}`) atlas.interiorId = null;
    for (const id of a.colliderIds) this.physics.removeStaticCollider(id);
    for (const c of a.crew) if (c.actor) { c.actor.mixer.stopAllAction(); c.actor = null; } // roots are shared-asset clones: the deferred tree dispose skips their buffers
    this.scene.remove(a.group);
    deferDisposeTree(a.group, 350);
    for (const rel of a.releases) rel();
    this.setPrompt(null);
    this.nearCrew = null;
  }

  dispose(): void {
    this.teardown();
    this.prompt?.remove();
    this.prompt = null;
  }
}

/**
 * The generated crew figures face +Z in model space — VERIFIED BY EYE, all
 * eight, with `verify/render-glb-views.mjs` (front/back contact sheet,
 * 2026-09-03). Ship-local yaw 0 is the bow (+Z), so no turn. Do NOT infer
 * this from geometry: a toe/nose protrusion heuristic read four of the
 * eight backwards (skirts, coats and satchels fool it) — re-render and look
 * whenever a new figure lands.
 */
const CREW_FIGURE_FACING = 0;

/** Shortest signed angle from a to b's delta (wraps ±π). */
function shortAngle(d: number): number {
  return ((d + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
}

/** City display name (crew lib keeps its own cache; tiny local mirror). */
const cityNames = new Map<string, Map<number, string>>();
function cityNameOf(seed: string, site: SettlementSite): string {
  let m = cityNames.get(seed);
  if (!m) { m = settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss)); cityNames.set(seed, m); }
  return m.get(site.id) ?? 'the city';
}
